/**
 * RecoverCann Prescription Validation
 * Real-time cart and product validation for cannabis compliance
 * OPTIMIZED: Preload on page load + instant local DFE recalculation
 * v2.4.0 — 2025-02-06 Remix-aligned warning format
 */

const RecoverCann = (function() {
  'use strict';

  // ===== DEBUG UTILITY =====
  const DEBUG = window.location.search.includes('rc_debug=true');
  const log = (...args) => { if (DEBUG) console.log('[RecoverCann]', ...args); };

  // ===== CONSTANTS =====
  const DEFAULT_API_BASE = 'https://recovercann-shopify.vercel.app';
  const CACHE_TTL_MS = 30000; // 30 seconds
  const CATEGORY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const DEBOUNCE_DELAY_MS = 300;
  const SINGLE_ORDER_LIMIT_GRAMS = 150;
  const STABLE_UI_MODE = true; // Prioritize consistency over speed for normal users
  const STABLE_REFRESH_DELAY_MS = 150;

  // Fail-closed checkout guard: checkout is blocked until validation explicitly passes.
  const CHECKOUT_TRIGGER_SELECTOR = [
    '[name="checkout"]',
    '.cart__checkout-button',
    '[href*="/checkout"]',
    'button[type="submit"].cart-drawer__checkout'
  ].join(', ');

  const checkoutGuardState = {
    initialized: false,
    canCheckout: false,
    isLoading: true,
  };

  function canProceedToCheckout() {
    return checkoutGuardState.initialized && !checkoutGuardState.isLoading && checkoutGuardState.canCheckout;
  }

  function applyCheckoutGuardVisualState() {
    const locked = !canProceedToCheckout();
    document.documentElement.classList.toggle('recovercann-checkout-locked', locked);

    const acceleratedContainers = document.querySelectorAll(
      '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
    );
    acceleratedContainers.forEach((container) => {
      container.style.pointerEvents = locked ? 'none' : '';
      container.style.opacity = locked ? '0.5' : '';
      container.setAttribute('aria-hidden', locked ? 'true' : 'false');
    });
  }

  function setCheckoutGuardState({ canCheckout, isLoading, initialized = true }) {
    checkoutGuardState.initialized = initialized;
    checkoutGuardState.canCheckout = Boolean(canCheckout);
    checkoutGuardState.isLoading = Boolean(isLoading);
    applyCheckoutGuardVisualState();
  }

  function installCheckoutGuards() {
    if (window._recovercannCheckoutGuardInstalled) return;
    window._recovercannCheckoutGuardInstalled = true;

    // Locked by default until validator confirms a valid state.
    applyCheckoutGuardVisualState();

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      // Block native checkout triggers.
      const checkoutTrigger = target.closest(CHECKOUT_TRIGGER_SELECTOR);
      if (checkoutTrigger && !canProceedToCheckout()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Block accelerated checkout containers while locked.
      const acceleratedTarget = target.closest(
        '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
      );
      if (acceleratedTarget && !canProceedToCheckout()) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    document.addEventListener('submit', (event) => {
      if (canProceedToCheckout()) return;

      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      const submitter = event.submitter instanceof Element ? event.submitter : null;
      const isCheckoutSubmitter = submitter ? submitter.matches(CHECKOUT_TRIGGER_SELECTOR) : false;
      const activeElement = document.activeElement instanceof Element ? document.activeElement : null;
      const activeIsCheckout = activeElement ? activeElement.matches(CHECKOUT_TRIGGER_SELECTOR) : false;

      if (isCheckoutSubmitter || activeIsCheckout) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }
  
  // ===== SAFE STORAGE HELPERS =====
  // sessionStorage can throw in private browsing mode on some browsers
  function safeSessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      console.warn('[RecoverCann] sessionStorage unavailable:', e);
      return null;
    }
  }
  
  function safeSessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('[RecoverCann] sessionStorage unavailable:', e);
      return false;
    }
  }
  
  function safeSessionRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('[RecoverCann] sessionStorage unavailable:', e);
    }
  }

  function safeLocalGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('[RecoverCann] localStorage unavailable:', e);
      return null;
    }
  }

  function safeLocalSet(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('[RecoverCann] localStorage unavailable:', e);
      return false;
    }
  }
  
  // ===== API CONFIGURATION =====
  const getApiBase = () => {
    const cartStatus = document.querySelector('.recovercann-cart-status, #recovercann-cart-status');
    const productValidator = document.getElementById('recovercann-product-validator');
    return cartStatus?.dataset?.apiBase || productValidator?.dataset?.apiBase || DEFAULT_API_BASE;
  };

  const API_BASE = getApiBase();
  const API_URL = `${API_BASE}/api/cart/check`;
  const RESERVATION_SESSION_STORAGE_KEY = 'recovercann_reservation_session_id';

  function createReservationSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `rc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function getReservationSessionId() {
    const existing = safeLocalGet(RESERVATION_SESSION_STORAGE_KEY);
    if (existing) return existing;

    const generated = createReservationSessionId();
    safeLocalSet(RESERVATION_SESSION_STORAGE_KEY, generated);
    return generated;
  }

  async function postCartValidation(requestBody, headers = {}) {
    const requestHeaders = {
      'Content-Type': 'application/json',
      'X-RecoverCann-Session-Id': getReservationSessionId(),
      ...headers,
    };

    return fetch(API_URL, {
      method: 'POST',
      headers: requestHeaders,
      cache: 'no-store',
      body: JSON.stringify(requestBody),
    });
  }
  
  // OPTIMIZATION: Cache validation results
  const validationCache = {
    data: null,
    cartHash: null,
    timestamp: 0,
    TTL: CACHE_TTL_MS
  };
  
  // OPTIMIZATION: Cache DFE values per variant for instant local recalculation
  const dfeCache = {
    variants: new Map(), // variant_id -> dfePerUnit
    prescription: null,  // { monthlyLimit, remainingInPeriod, usedInPeriod, periodEnd }
    customerId: null,    // Track which customer this cache is for
    initialized: false
  };
  
  // OPTIMIZATION: Cache category limit data for instant local recalculation
  const categoryCache = {
    productMap: new Map(),   // product_id (numeric) -> [category handle strings]
    categories: new Map(),   // handle -> { title, limit, used }
    initialized: false,
    timestamp: 0
  };
  
  // PRELOAD: Track if preload is in progress or complete
  let preloadPromise = null;
  let preloadComplete = false;
  
  // OPTIMIZATION: Debounce utility
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Retry + in-flight dedup live in recovercann-cart-fetch.js (loaded first),
  // shared across every module that needs a fresh cart read so a single
  // "add to cart" doesn't fire a burst of concurrent /cart.js requests.
  async function fetchFreshCart() {
    return window.RecoverCannFetchCart();
  }

  
  function normalizeCustomerId(id) {
    if (!id) return '';
    if (typeof id === 'string' && id.includes('/')) return id.split('/').pop();
    return String(id);
  }

  // Generate full cart hash including quantities AND active customer
  function hashCart(cartData, customerId) {
    const itemsHash = cartData.items.map(i => `${i.variant_id}:${i.quantity}`).join('|');
    const normalizedId = normalizeCustomerId(customerId);
    return `${normalizedId}::${itemsHash}`;
  }

  function buildCacheKeyForRequest(customerId, cartItems) {
    const normalizedId = normalizeCustomerId(customerId);
    const itemsHash = cartItems
      .map(item => `${item.variantId}:${item.quantity}`)
      .sort()
      .join('|');
    return `${normalizedId}::${itemsHash}`;
  }

  function isCategoryCacheFresh() {
    if (!categoryCache.initialized) return false;
    const age = Date.now() - categoryCache.timestamp;
    if (age <= CATEGORY_CACHE_TTL_MS) return true;
    categoryCache.categories.clear();
    categoryCache.productMap.clear();
    categoryCache.initialized = false;
    categoryCache.timestamp = 0;
    return false;
  }
  
  // ===== CART VALIDATION =====
  let cartValidatorInstances = [];
  let cartValidatorInstanceCounter = 0;
  const CART_STATUS_CONTAINER_SELECTOR = '.recovercann-cart-status[data-customer-id], [id="recovercann-cart-status"][data-customer-id]';

  function getCartStatusContainers() {
    return Array.from(document.querySelectorAll(CART_STATUS_CONTAINER_SELECTOR));
  }

  function getCartStatusContainerByInstanceId(instanceId) {
    if (!instanceId) return null;
    return getCartStatusContainers().find((container) => container.dataset.rcInstanceId === String(instanceId)) || null;
  }

  function hasRenderableCartStatus(container) {
    if (!container) return false;
    return Boolean(
      container.querySelector('.recovercann-loading') &&
      container.querySelector('.recovercann-content')
    );
  }

  function getCartStatusContainerForContext(context) {
    if (!context) return null;
    const containers = getCartStatusContainers();
    if (!containers.length) return null;

    const exactRenderable = containers.find((container) =>
      container.dataset.rcContext === context && hasRenderableCartStatus(container)
    );
    if (exactRenderable) return exactRenderable;

    const exact = containers.find((container) => container.dataset.rcContext === context);
    if (exact && hasRenderableCartStatus(exact)) return exact;

    const anyRenderable = containers.find((container) => hasRenderableCartStatus(container));
    if (anyRenderable) return anyRenderable;

    // Last resort: return exact context container even if not yet renderable.
    // Callers can retry hydration.
    if (exact) return exact;

    if (context === 'cart') {
      const drawer = containers.find((container) => container.dataset.rcContext === 'drawer');
      if (drawer) return drawer;
    }

    return containers[0] || null;
  }

  function getValidatorDebugState(validator, label = '') {
    if (!validator) return { label, exists: false };
    return {
      label,
      instanceId: validator.instanceId,
      context: validator.context,
      customerId: validator.customerId,
      hasContainer: Boolean(validator.container),
      hasElements: Boolean(validator.elements),
      hasContent: Boolean(validator.elements?.content),
      hasWarnings: Boolean(validator.elements?.warnings),
      hasRemaining: Boolean(validator.elements?.remaining),
      hasInCart: Boolean(validator.elements?.inCart),
      hasBar: Boolean(validator.elements?.bar),
      hasMarker: Boolean(validator.elements?.marker),
      hasPeriodInfo: Boolean(validator.elements?.periodInfo),
      containerCount: getCartStatusContainers().length,
    };
  }
  
  class CartValidator {
    constructor(container = null) {
      installCheckoutGuards();
      this.container = container && container.matches?.(CART_STATUS_CONTAINER_SELECTOR)
        ? container
        : document.querySelector(CART_STATUS_CONTAINER_SELECTOR);
      if (!this.container) {
        log('CartValidator ctor: no container', getValidatorDebugState(this, 'ctor:no-container'));
        return;
      }
      if (this.container.dataset.rcValidatorInitialized === 'true') {
        log('CartValidator ctor: already initialized', getValidatorDebugState(this, 'ctor:already-initialized'));
        return;
      }
      this.container.dataset.rcValidatorInitialized = 'true';
      if (!this.container.dataset.rcInstanceId) {
        cartValidatorInstanceCounter += 1;
        this.container.dataset.rcInstanceId = String(cartValidatorInstanceCounter);
      }
      this.instanceId = this.container.dataset.rcInstanceId;
      this.context = this.container.dataset.rcContext || 'cart';
      
      this.customerId = this.container.dataset.customerId;
      if (!this.customerId) return;

      this.activeCustomerId = this.customerId;
      log('CartValidator ctor: initialized', getValidatorDebugState(this, 'ctor:initialized'));
      
      // Mark as successfully initialized
      this.initialized = true;
      
      this.refreshElements();
      
      // OPTIMIZATION: Debounced refresh function
      this.debouncedRefresh = debounce(() => this.refresh(), 300);
      // Timer for background API refresh (category limits) — uses cart data directly
      this._apiRefreshTimer = null;
      // Generation counter: increments on every optimistic update.
      // Stale fetch responses (from earlier generations) are ignored.
      this._generation = 0;
      this.isSuperShopperActive = false;
      this._refreshTimer = null;
      this._elementRetryCount = 0;
      this._elementRetryTimer = null;
      this._lastCartUpdateEventTs = 0;
      this._cartUpdateBridgeTimer = null;
      this._statusLoadingTimer = null;
      this._statusLoadingDelayMs = 220;
      this._stockRemovalInFlight = false;

      // Fail closed immediately while we fetch validation.
      setCheckoutGuardState({ canCheckout: false, isLoading: true, initialized: true });
      
      this.init();
    }
    
    // Re-query all DOM elements (needed after morphSection replaces the HTML)
    refreshElements() {
      const scopedContainer = getCartStatusContainerByInstanceId(this.instanceId);
      const contextContainer = getCartStatusContainerForContext(this.context);
      const firstRenderable = getCartStatusContainers().find((container) => hasRenderableCartStatus(container));
      this.container = scopedContainer || contextContainer || firstRenderable || document.querySelector(CART_STATUS_CONTAINER_SELECTOR);
      if (!this.container) {
        log('refreshElements: no container', getValidatorDebugState(this, 'refresh:no-container'));
        return false;
      }

      if (this.instanceId && !this.container.dataset.rcInstanceId) {
        this.container.dataset.rcInstanceId = String(this.instanceId);
      }
      
      const loading = this.container.querySelector('.recovercann-loading');
      const content = this.container.querySelector('.recovercann-content');
      
      // Must have at least loading and content elements
      if (!loading || !content) {
        const fallbackContainer = getCartStatusContainers().find((container) =>
          hasRenderableCartStatus(container) &&
          (container.dataset.rcContext === this.context || (this.context === 'cart' && container.dataset.rcContext === 'drawer'))
        );

        if (fallbackContainer && fallbackContainer !== this.container) {
          this.container = fallbackContainer;
          if (this.instanceId && !this.container.dataset.rcInstanceId) {
            this.container.dataset.rcInstanceId = String(this.instanceId);
          }
          return this.refreshElements();
        }

        log('refreshElements: missing loading/content', {
          ...getValidatorDebugState(this, 'refresh:missing-loading-content'),
          hasLoading: Boolean(loading),
          hasContentNode: Boolean(content),
        });
        return false;
      }
      
      this.elements = {
        loading: loading,
        content: content,
        error: this.container.querySelector('.recovercann-error'),
        warnings: this.container.querySelector('.recovercann-warnings'),
        remaining: this.container.querySelector('[data-rc="remaining"], #recovercann-remaining'),
        inCart: this.container.querySelector('[data-rc="in-cart"], #recovercann-in-cart'),
        bar: this.container.querySelector('[data-rc="bar"], #recovercann-bar'),
        marker: this.container.querySelector('[data-rc="marker"], #recovercann-marker'),
        periodInfo: this.container.querySelector('[data-rc="period-info"], #recovercann-period-info'),
        largeWarning: this.container.querySelector('[data-rc="large-warning"], #recovercann-large-warning'),
        categoryLimits: this.container.querySelector('[data-rc="category-limits"], #recovercann-category-limits'),
        subtotal: this.container.querySelector('[data-rc="subtotal"], #recovercann-subtotal')
      };
      this._elementRetryCount = 0;
      if (this._elementRetryTimer) {
        clearTimeout(this._elementRetryTimer);
        this._elementRetryTimer = null;
      }
      log('refreshElements: success', getValidatorDebugState(this, 'refresh:success'));
      return true;
    }

    queueElementRetry(reason = 'missing-elements') {
      if (this._elementRetryTimer) return;
      if (this._elementRetryCount >= 6) {
        log('elements:retry-give-up', {
          ...getValidatorDebugState(this, 'elements:retry-give-up'),
          reason,
          retries: this._elementRetryCount,
        });
        return;
      }

      this._elementRetryCount += 1;
      const delayMs = Math.min(100 + (this._elementRetryCount * 75), 500);
      this._elementRetryTimer = setTimeout(() => {
        this._elementRetryTimer = null;
        const refreshed = this.refreshElements();
        if (!refreshed) {
          this.queueElementRetry(reason);
          return;
        }

        if (this.lastCartData) {
          this.validateCart(this.lastCartData, false, true);
          return;
        }

        this.refresh(true);
      }, delayMs);
    }
    

    
    refresh(forceApiCall = false, options = {}) {
      const showLoading = options.showLoading !== false;
      if (showLoading) {
        this.setStatusLoading(true);
      }
      this.fetchCart().then(async cartData => {
        if (cartData) {
          await this.validateCart(cartData, false, forceApiCall);
          this.updateSubtotal(cartData);
        }
      }).catch(() => {
        // Swallow fetch/validation errors but clear loading state.
      }).finally(() => {
        // Always clear loading state once refresh completes.
        if (showLoading) {
          this.setStatusLoading(false);
        }
      });
    }

    setStatusLoading(isLoading) {
      if (!this.container || !this.elements?.content) {
        this.refreshElements();
      }
      if (!this.container || !this.elements?.content) return;

      if (isLoading) {
        if (this.elements.error) {
          this.elements.error.style.display = 'none';
        }
        if (this._statusLoadingTimer || this.container.classList.contains('is-loading')) {
          return;
        }

        this._statusLoadingTimer = setTimeout(() => {
          this._statusLoadingTimer = null;
          if (!this.container || !this.elements?.content) return;
          this.container.classList.add('is-loading');
          this.elements.content.dataset.loading = 'true';
          if (this.elements.loading) {
            this.elements.loading.style.display = 'flex';
          }
          this.setCheckoutLoading(true);
        }, this._statusLoadingDelayMs);
        return;
      }

      if (this._statusLoadingTimer) {
        clearTimeout(this._statusLoadingTimer);
        this._statusLoadingTimer = null;
      }

      this.container.classList.remove('is-loading');
      this.elements.content.dataset.loading = 'false';
      if (this.elements.loading) {
        this.elements.loading.style.display = 'none';
      }
      this.setCheckoutLoading(false);
      if (this.elements.content) {
        this.elements.content.style.display = 'block';
      }
    }

    scheduleRefresh(forceApiCall = false, options = {}) {
      if (!STABLE_UI_MODE) {
        this.refresh(forceApiCall, options);
        return;
      }
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => {
        this.refresh(forceApiCall, options);
      }, STABLE_REFRESH_DELAY_MS);
    }
    
    // Update subtotal from cart data
    updateSubtotal(cartData) {
      if (!this.elements.subtotal) return;
      
      // Format price (Shopify stores in cents)
      const formatted = this.formatMoney(cartData.total_price);
      this.elements.subtotal.textContent = formatted;
    }
    
    // Format money using Shopify's format
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      // Use store's currency format - default to $ prefix
      return '$' + amount;
    }
    
    async init() {
      const cartData = await this.fetchCart();
      if (!cartData) {
        this.showError('Unable to load cart');
        return;
      }

      
      // OPTIMIZATION: Wait for preload if it's in progress, then use cached data
      if (preloadPromise && !preloadComplete) {
        await preloadPromise;
      }
      
      // If preload succeeded, we already have cached data - no loading spinner needed
      const isFirstLoad = !preloadComplete;
      await this.validateCart(cartData, isFirstLoad);
      this.setupCartListeners();
      this.setupStockWarningActions();
    }
    
    async fetchCart() {
      try {
        const cartData = await fetchFreshCart();
        // Cache cart data for optimistic updates
        this.lastCartData = cartData;
        return cartData;
      } catch (error) {
        console.error('Failed to fetch cart:', error);
        return null;
      }
    }
    
    // Check if we can use local recalculation (all variants have cached DFE values)
    // Also verify cache is for the current target customer
    canRecalculateLocally(cartData) {
      if (STABLE_UI_MODE) return false;
      if (this.isSuperShopperActive) return false;
      if (!dfeCache.initialized || !dfeCache.prescription) return false;
      
      // Check if cache is for the current target customer
      const currentTargetId = this.activeCustomerId || this.customerId;
      if (dfeCache.customerId && dfeCache.customerId !== currentTargetId) {
        log(' DFE cache customer mismatch, skipping local recalc');
        return false;
      }
      
      return cartData.items.every(item => dfeCache.variants.has(item.variant_id));
    }
    
    // Instant local recalculation without API call
    recalculateLocally(cartData) {
      let totalDFE = 0;
      const items = cartData.items.map(item => {
        const dfePerUnit = dfeCache.variants.get(item.variant_id) || 0;
        const itemDfe = dfePerUnit * item.quantity;
        totalDFE += itemDfe;
        return { variantId: item.variant_id, quantity: item.quantity, dfePerUnit, totalDfe: itemDfe };
      });
      
      const rx = dfeCache.prescription;
      const afterThisCart = rx.remainingInPeriod - totalDFE;
      const isWithinPeriodLimit = afterThisCart >= 0;
      const isWithinOrderLimit = totalDFE <= SINGLE_ORDER_LIMIT_GRAMS;
      
      const errors = [];
      if (!isWithinPeriodLimit) {
        errors.push(`Exceeds 30-day prescription limit (${rx.monthlyLimit}g). You have ${rx.remainingInPeriod.toFixed(1)}g remaining, but this cart contains ${totalDFE.toFixed(1)}g.`);
      }
      if (!isWithinOrderLimit) {
        errors.push(`Single order limit is ${SINGLE_ORDER_LIMIT_GRAMS}g. This cart contains ${totalDFE.toFixed(1)}g.`);
      }
      
      // Locally recalculate category limits if cache is available
      let categoryLimitsLocal = null;
      let isWithinCategoryLimits = validationCache.data?.limits?.isWithinCategoryLimits !== false;
      if (isCategoryCacheFresh()) {
        // Count cart quantities per product_id
        const cartProductQty = new Map();
        for (const item of cartData.items) {
          const pId = item.product_id;
          if (pId) {
            cartProductQty.set(pId, (cartProductQty.get(pId) || 0) + item.quantity);
          }
        }
        
        // Calculate inCart per category
        const categoryInCart = new Map();
        for (const [productId, handles] of categoryCache.productMap) {
          const qty = cartProductQty.get(productId) || 0;
          for (const handle of handles) {
            categoryInCart.set(handle, (categoryInCart.get(handle) || 0) + qty);
          }
        }
        
        // Build category limits array
        categoryLimitsLocal = [];
        for (const [handle, data] of categoryCache.categories) {
          const inCart = categoryInCart.get(handle) || 0;
          if (data.used > 0 || inCart > 0) {
            const total = data.used + inCart;
            categoryLimitsLocal.push({
              title: data.title,
              handle,
              limit: data.limit,
              used: data.used,
              inCart,
              remaining: Math.max(0, data.limit - data.used),
              exceeds: total > data.limit,
            });
          }
        }
        isWithinCategoryLimits = categoryLimitsLocal.every(c => !c.exceeds);
      }
      
      const isWithinTHCCap = validationCache.data?.limits?.isWithinTHCCap !== false;
      const stockValidation = this.validateCartStock(cartData);
      const cartItemCount = cartData.items.reduce((sum, i) => sum + i.quantity, 0);
      const canCheckout = cartItemCount > 0 && isWithinPeriodLimit && isWithinOrderLimit && isWithinCategoryLimits && isWithinTHCCap && stockValidation.isWithinStock;
      
      // Update the cached validation with new cart totals
      const updatedValidation = {
        ...validationCache.data,
        valid: canCheckout,
        canCheckout,
        errors: this.mergeStockErrors(errors, stockValidation),
        cart: {
          totalDFE,
          itemCount: cartItemCount,
          items
        },
        limits: {
          ...validationCache.data.limits,
          isWithinPeriodLimit,
          isWithinOrderLimit,
          isWithinCategoryLimits,
          isWithinStock: stockValidation.isWithinStock,
          afterThisCart,
          ...(categoryLimitsLocal !== null ? { categoryLimits: categoryLimitsLocal } : {})
        },
        stock: stockValidation
      };
      
      return updatedValidation;
    }

    validateCartStock(cartData) {
      const items = Array.isArray(cartData?.items) ? cartData.items : [];
      const outOfStockItems = items.filter((item) => item?.available === false);

      const domWarningEntries = Array.from(document.querySelectorAll('.cart-items__warning, .cart-item-drawer__warning'))
        .map((node) => {
          const message = (node.textContent || '').trim();
          const lineKey = node.closest('[data-key]')?.getAttribute('data-key') || '';
          return { message, lineKey };
        })
        .filter((entry) => entry.message);

      const quantityOverMaxEntries = Array.from(document.querySelectorAll('cart-quantity-selector-component input[name="updates[]"][max]'))
        .map((input) => {
          const value = Number(input.value);
          const max = Number(input.getAttribute('max'));
          if (!Number.isFinite(value) || !Number.isFinite(max) || value <= max) return null;

          const line = input.closest('.cart-items__table-row, .cart-item-drawer, .cart-items__item');
          const lineKey = line?.closest('[data-key]')?.getAttribute('data-key') || '';
          const variantIdRaw = line?.querySelector('cart-quantity-selector-component')?.getAttribute('data-variant-id') || '';
          const variantId = Number(variantIdRaw);
          const title = line?.querySelector('.cart-items__name, .cart-item-drawer__title a, .cart-item-drawer__title')?.textContent?.trim();
          const message = title
            ? `Only ${max} of "${title}" is available in stock.`
            : `One cart line exceeds available stock (${value} requested, ${max} available).`;
          return {
            message,
            lineKey,
            variantId: Number.isFinite(variantId) ? variantId : null,
          };
        })
        .filter(Boolean);

      const hasDomStockWarnings = domWarningEntries.length > 0 || quantityOverMaxEntries.length > 0;

      const removableLineItemKeys = Array.from(new Set([
        ...outOfStockItems.map((item) => item?.key).filter(Boolean),
        ...domWarningEntries.map((entry) => entry.lineKey).filter(Boolean),
        ...quantityOverMaxEntries.map((entry) => entry.lineKey).filter(Boolean),
      ]));

      const removableVariantIds = Array.from(new Set([
        ...outOfStockItems.map((item) => Number(item?.variant_id)).filter((id) => Number.isFinite(id)),
        ...quantityOverMaxEntries.map((entry) => Number(entry?.variantId)).filter((id) => Number.isFinite(id)),
      ]));

      if (!outOfStockItems.length && !hasDomStockWarnings) {
        return {
          isWithinStock: true,
          outOfStockItems: [],
          messages: [],
          removableLineItemKeys: [],
          removableVariantIds: [],
        };
      }

      const itemNames = outOfStockItems
        .map((item) => item?.product_title || item?.title)
        .filter(Boolean)
        .slice(0, 3);

      const baseMessage = 'One or more items in your bag are out of stock. Remove unavailable items to continue to checkout.';
      const detailedMessage = itemNames.length > 0
        ? `${baseMessage} (${itemNames.join(', ')})`
        : baseMessage;

      const messages = [
        ...domWarningEntries.map((entry) => entry.message),
        ...quantityOverMaxEntries.map((entry) => entry.message),
      ];

      if (outOfStockItems.length > 0 || messages.length === 0) {
        messages.unshift(detailedMessage);
      }

      return {
        isWithinStock: false,
        outOfStockItems,
        messages,
        removableLineItemKeys,
        removableVariantIds,
      };
    }

    setupStockWarningActions() {
      if (this._stockWarningActionsBound) return;
      this._stockWarningActionsBound = true;

      document.addEventListener('click', async (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const action = target.closest('[data-rc-remove-out-of-stock="true"]');
        if (!action) return;

        const container = action.closest(CART_STATUS_CONTAINER_SELECTOR);
        if (!container || container.dataset.rcInstanceId !== String(this.instanceId)) return;

        event.preventDefault();

        const lineKeysRaw = action.getAttribute('data-rc-line-keys') || '';
        const variantIdsRaw = action.getAttribute('data-rc-variant-ids') || '';

        const lineKeys = lineKeysRaw
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean)
          .map((value) => decodeURIComponent(value));

        const variantIds = variantIdsRaw
          .split('|')
          .map((value) => Number(value.trim()))
          .filter((value) => Number.isFinite(value));

        await this.removeOutOfStockItemsFromCart({ lineKeys, variantIds, trigger: action });
      }, true);
    }

    async removeOutOfStockItemsFromCart({ lineKeys = [], variantIds = [], trigger = null } = {}) {
      if (this._stockRemovalInFlight) return;
      if (!lineKeys.length && !variantIds.length) return;

      this._stockRemovalInFlight = true;
      const originalText = trigger?.textContent || '';
      if (trigger) {
        trigger.textContent = 'Removing…';
        trigger.setAttribute('aria-disabled', 'true');
      }

      try {
        const updates = {};
        lineKeys.forEach((lineKey) => {
          updates[lineKey] = 0;
        });

        if (!lineKeys.length) {
          variantIds.forEach((variantId) => {
            updates[String(variantId)] = 0;
          });
        }

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });

        if (!response.ok) {
          throw new Error(`Failed to remove out-of-stock items (${response.status})`);
        }

        const cartData = await this.fetchCart();
        if (cartData) {
          await this.validateCart(cartData, false, true);
          this.updateSubtotal(cartData);

          const sections = await this.fetchCartSections();
          if (sections) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              detail: {
                resource: cartData,
                sourceId: 'recovercann-validation',
                data: {
                  source: 'recovercann-validation',
                  itemCount: Number(cartData.item_count || 0),
                  sections,
                },
                forceRefresh: true,
              },
            }));
          } else {
            document.dispatchEvent(new CustomEvent('cart:update', {
              detail: {
                sourceId: 'recovercann-validation',
                forceRefresh: true,
              },
            }));
          }
        }
      } catch (error) {
        console.error('[RecoverCann] Failed to remove out-of-stock items', error);
      } finally {
        this._stockRemovalInFlight = false;
        if (trigger) {
          trigger.textContent = originalText || 'Remove out-of-stock items';
          trigger.removeAttribute('aria-disabled');
        }
      }
    }

    async fetchCartSections() {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((node) => node?.getAttribute('data-section-id'))
        .filter(Boolean);

      if (!sectionIds.length) return null;

      const endpoint = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;

      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache',
          },
          cache: 'no-store',
        });

        if (!response.ok) return null;
        const payload = await response.json();
        return payload && typeof payload === 'object' ? payload : null;
      } catch {
        return null;
      }
    }

    mergeStockErrors(existingErrors, stockValidation) {
      const normalized = Array.isArray(existingErrors) ? [...existingErrors] : [];

      if (!stockValidation || stockValidation.isWithinStock) {
        return normalized.length > 0 ? normalized : null;
      }

      for (const message of stockValidation.messages || []) {
        if (!normalized.includes(message)) {
          normalized.push(message);
        }
      }

      return normalized.length > 0 ? normalized : null;
    }
    
    async validateCart(cartData, isFirstLoad = false, forceApiCall = false) {
      if (!this.elements || !this.elements.content) {
        const refreshed = this.refreshElements();
        if (!refreshed) {
          log('validateCart:skip-missing-elements', getValidatorDebugState(this, 'validate:skip-missing-elements'));
          this.queueElementRetry('validate');
          return;
        }
      }
      log('validateCart:start', {
        ...getValidatorDebugState(this, 'validate:start'),
        isFirstLoad,
        forceApiCall,
        cartItemCount: cartData?.items?.length || 0,
      });
      if (DEBUG) {
        forceApiCall = true;
      }
      if (STABLE_UI_MODE) {
        forceApiCall = true;
      }
      if (this.isSuperShopperActive) {
        forceApiCall = true;
      }
      const currentHash = hashCart(cartData, this.activeCustomerId || this.customerId);
      const now = Date.now();
      
      // INSTANT UPDATE: If we have cached DFE values, calculate and render immediately
      // Skip this if forceApiCall is true (need fresh category limits from API)
      if (!forceApiCall && dfeCache.initialized && this.canRecalculateLocally(cartData)) {
        const localValidation = this.recalculateLocally(cartData);
        this.renderValidation(localValidation, true); // true = skip loading toggle
        this.updateCheckoutButton(localValidation.canCheckout);
        
        // Update cache with local result
        validationCache.data = localValidation;
        validationCache.cartHash = currentHash;
        validationCache.timestamp = now;
        return; // No API call needed!
      }
      
      // Full cache hit - exact same cart (skip if forceApiCall)
      if (
        !forceApiCall &&
        validationCache.data &&
        validationCache.cartHash === currentHash &&
        now - validationCache.timestamp < validationCache.TTL
      ) {
        const cachedValidation = { ...validationCache.data };
        const stockValidation = this.validateCartStock(cartData);

        cachedValidation.limits = {
          ...(cachedValidation.limits || {}),
          isWithinStock: stockValidation.isWithinStock,
        };
        cachedValidation.stock = stockValidation;

        if (!stockValidation.isWithinStock) {
          cachedValidation.canCheckout = false;
          cachedValidation.valid = false;
          cachedValidation.errors = this.mergeStockErrors(cachedValidation.errors, stockValidation);
        }

        if (!this.hasItemsInCart(cachedValidation)) {
          cachedValidation.canCheckout = false;
          cachedValidation.valid = false;
        }

        this.renderValidation(cachedValidation);
        this.updateCheckoutButton(cachedValidation.canCheckout);
        return;
      }
      
      // No loading spinner - content stays visible with placeholder values
      // Just hide any previous error
      if (this.elements?.error) this.elements.error.style.display = 'none';
      
      try {
        const cartItems = cartData.items.map(item => ({
          variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
          productId: `gid://shopify/Product/${item.product_id}`,
          quantity: item.quantity
        }));
        
        const requestBody = {
          customerId: `gid://shopify/Customer/${this.customerId}`,
          cartItems,
          ...(DEBUG ? { debug: true } : {})
        };
        
        const cacheKey = buildCacheKeyForRequest(this.customerId, cartItems);
        const response = await postCartValidation(requestBody, {
          'X-RecoverCann-Cache-Key': cacheKey,
        });

        log('validateCart:response', {
          ...getValidatorDebugState(this, 'validate:response'),
          ok: response.ok,
          status: response.status,
        });
        
        if (!response.ok) {
          const responseText = await response.text().catch(() => '');
          const detail = responseText ? ` | body: ${responseText.slice(0, 500)}` : '';
          throw new Error(`Validation failed (${response.status})${detail}`);
        }
        
        const validation = await response.json();
        const stockValidation = this.validateCartStock(cartData);
        validation.limits = {
          ...(validation.limits || {}),
          isWithinStock: stockValidation.isWithinStock,
        };
        validation.stock = stockValidation;
        if (!stockValidation.isWithinStock) {
          validation.canCheckout = false;
          validation.valid = false;
          validation.errors = this.mergeStockErrors(validation.errors, stockValidation);
        }

        if (!this.hasItemsInCart(validation)) {
          validation.canCheckout = false;
          validation.valid = false;
        }
        log('validateCart:payload', {
          hasPeriod: Boolean(validation?.period),
          hasCart: Boolean(validation?.cart),
          hasLimits: Boolean(validation?.limits),
          hasWarnings: Array.isArray(validation?.warnings),
          hasSuperShopper: Boolean(validation?.superShopper),
        });
        if (DEBUG) {
          console.log('[RecoverCann] cart/check response:', {
            ok: response.ok,
            status: response.status,
            hasPeriod: Boolean(validation?.period),
            hasCart: Boolean(validation?.cart),
            superShopper: validation?.superShopper || null,
            errors: validation?.errors || null,
          });
        }

        if (this.maybeAutoDisableSuperShopper(validation)) {
          return;
        }
        if (DEBUG && validation._debug) {
          log(' cart/check debug:', validation._debug);
        }
        if (DEBUG && !validation._debug) {
          console.warn('[RecoverCann] debug flag set but _debug missing from cart/check response');
        }
        if (DEBUG || validation.superShopper?.enabled) {
          console.log('[RecoverCann] cart/check superShopper:', {
            superShopper: validation.superShopper || null,
            debug: validation._debug || null,
          });
        }
        
        const newIsSuperShopper = Boolean(validation?.superShopper?.enabled);
        const apiCustomerId = validation.superShopper?.customerId || this.customerId;
        const newActiveCustomerId = normalizeCustomerId(apiCustomerId);
        const stateChanged = this.isSuperShopperActive !== newIsSuperShopper;
        const customerChanged = this.activeCustomerId && this.activeCustomerId !== newActiveCustomerId;

        if (stateChanged || customerChanged) {
          validationCache.data = null;
          validationCache.cartHash = null;
          validationCache.timestamp = 0;
          dfeCache.initialized = false;
          dfeCache.prescription = null;
          dfeCache.customerId = null;
          categoryCache.initialized = false;
          categoryCache.timestamp = 0;
        }

        this.isSuperShopperActive = newIsSuperShopper;
        this.activeCustomerId = newActiveCustomerId;

        // OPTIMIZATION: Cache the result and populate DFE cache
        validationCache.data = validation;
        validationCache.cartHash = currentHash;
        validationCache.timestamp = Date.now();
        
        // Cache DFE values per variant for future local recalculations
        if (validation.cart && validation.cart.items) {
          validation.cart.items.forEach(item => {
            const variantId = parseInt(item.variantId.replace('gid://shopify/ProductVariant/', ''));
            dfeCache.variants.set(variantId, item.dfePerUnit || 0);
          });
        }
        
        // Cache prescription info and track which customer this is for
        if (validation.period && validation.prescription) {
          dfeCache.customerId = newActiveCustomerId;
          dfeCache.prescription = {
            monthlyLimit: validation.prescription.monthlyLimit,
            remainingInPeriod: validation.period.remainingInPeriod,
            usedInPeriod: validation.period.usedInPeriod,
            periodEnd: validation.period.endDate
          };
          dfeCache.initialized = true;
        }
        
        // Cache category limit data for local recalculation
        if (validation.limits?.categoryLimits) {
          categoryCache.categories.clear();
          for (const cat of validation.limits.categoryLimits) {
            categoryCache.categories.set(cat.handle, { title: cat.title, limit: cat.limit, used: cat.used });
          }
        }
        if (validation.limits?.productCategoryMap) {
          categoryCache.productMap.clear();
          for (const [gid, handles] of Object.entries(validation.limits.productCategoryMap)) {
            const productId = parseInt(gid.replace('gid://shopify/Product/', ''));
            categoryCache.productMap.set(productId, handles);
          }
          categoryCache.initialized = true;
          categoryCache.timestamp = Date.now();
        }
        
        this.renderValidation(validation);
        this.updateCheckoutButton(validation.canCheckout);
        log('validateCart:rendered', getValidatorDebugState(this, 'validate:rendered'));
        
      } catch (error) {
        console.error('Validation error:', {
          message: error?.message || String(error),
          customerId: this.customerId,
          context: this.context,
          apiUrl: API_URL,
          hasElements: Boolean(this.elements),
          hasContent: Boolean(this.elements?.content),
        });
        // Only show error on first load, silently fail on background updates
        if (isFirstLoad) {
          this.showError('Unable to validate cart');
        }
      }
    }
    
    renderValidation(validation, skipLoadingToggle = false) {
      // Ensure we have valid elements
      if (!this.elements || !this.elements.content) {
        const refreshed = this.refreshElements();
        if (!refreshed) {
          log('renderValidation:skip-missing-elements', getValidatorDebugState(this, 'render:skip-missing-elements'));
          this.queueElementRetry('render');
          return;
        }
      }
      const requiredElementsPresent = Boolean(
        this.elements?.content &&
        this.elements?.warnings &&
        this.elements?.remaining &&
        this.elements?.inCart &&
        this.elements?.periodInfo &&
        this.elements?.bar &&
        this.elements?.marker
      );
      if (!requiredElementsPresent) {
        log('renderValidation:skip-incomplete-elements', getValidatorDebugState(this, 'render:skip-incomplete-elements'));
        this.queueElementRetry('render-incomplete');
        return;
      }
      log('renderValidation:start', {
        ...getValidatorDebugState(this, 'render:start'),
        hasValidation: Boolean(validation),
        hasPeriod: Boolean(validation?.period),
        hasCart: Boolean(validation?.cart),
      });
      
      // Content is always visible by default in the Liquid template
      // No need to toggle visibility - just update values
      
      // Check for Super Shopper mode from API response only.
      // If sessionStorage says Super Shopper but API disagrees, clear the stale flag.
      const sessionSuperShopper = safeSessionGet('superShopperCustomerId');
      const apiSuperShopperEnabled = Boolean(validation.superShopper && validation.superShopper.enabled);
      if (!apiSuperShopperEnabled && sessionSuperShopper) {
        safeSessionRemove('superShopperCustomerId');
      }
      const isSuperShopperActive = apiSuperShopperEnabled;
      
      if (isSuperShopperActive) {
        if (validation.superShopper && validation.superShopper.enabled) {
          this.renderSuperShopperBanner(validation.superShopper);
        }
        this.replaceCheckoutWithDraftOrder(validation);
      } else {
        // Not in Super Shopper mode - restore checkout button if it was replaced
        this.restoreCheckoutButton();
      }
      
      // Render warnings/errors above the status grid.
      // For Super Shoppers: non-blocking cautions (yellow).
      // For normal shoppers: yellow warnings to match Remix, while category limit details are shown in the category banner.
      this.elements.warnings.innerHTML = '';
      const filterCategoryLimitMessages = (messages) =>
        (messages || []).filter(msg => !/Limit is .* per period\./i.test(msg));

      const warningIconMarkup = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      `;

      const addWarningBanner = (htmlContent, { caution = false } = {}) => {
        const warningDiv = document.createElement('div');
        warningDiv.className = caution ? 'recovercann-warning recovercann-caution' : 'recovercann-warning';
        warningDiv.innerHTML = `${warningIconMarkup}<p>${htmlContent}</p>`;
        this.elements.warnings.appendChild(warningDiv);
      };

      if (isSuperShopperActive && validation.warnings && validation.warnings.length > 0) {
        filterCategoryLimitMessages(validation.warnings).forEach(warning => {
          const escaped = warning.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          addWarningBanner(`${escaped} <em>(Super Shopper can proceed)</em>`, { caution: true });
        });
      }

      if (!isSuperShopperActive) {
        const stockMessages = validation.stock?.messages || [];
        const nonStockErrors = (validation.errors || []).filter((error) => !stockMessages.includes(error));
        const hasBlockingErrors = nonStockErrors.length > 0;

        if (hasBlockingErrors) {
          filterCategoryLimitMessages(nonStockErrors).forEach(error => {
            const escaped = error.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            addWarningBanner(`<strong>Prescription notice</strong>. ${escaped}`);
          });
        }

        if (validation.limits && validation.limits.isWithinStock === false) {
          const stockMessage = stockMessages[0]
            || 'One or more items in your bag are out of stock. Remove unavailable items to continue to checkout.';
          const escaped = stockMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const removableLineKeys = (validation.stock?.removableLineItemKeys || [])
            .filter(Boolean)
            .map((value) => encodeURIComponent(String(value)));
          const removableVariantIds = (validation.stock?.removableVariantIds || [])
            .filter((value) => Number.isFinite(Number(value)))
            .map((value) => String(value));
          const removeLink = (removableLineKeys.length > 0 || removableVariantIds.length > 0)
            ? ` <a href="#" data-rc-remove-out-of-stock="true" data-rc-line-keys="${removableLineKeys.join('|')}" data-rc-variant-ids="${removableVariantIds.join('|')}">Remove out-of-stock items</a>`
            : '';
          addWarningBanner(`<strong>Stock notice</strong>. ${escaped}${removeLink}`);
        }

        // Only show derived limit warnings when there are no blocking business-rule errors
        // (e.g. expired prescription), to avoid duplicate/conflicting notices.
        if (!hasBlockingErrors) {
          // Use Remix-style formatted warnings for each limit type
          if (validation.limits && validation.limits.isWithinOrderLimit === false) {
            addWarningBanner("<strong>Order limit exceeded</strong>. You cannot check out with more than 150g's of cannabis in your cart.");
          }
          if (validation.limits && validation.limits.isWithinPeriodLimit === false) {
            const remaining = validation.period?.remainingInPeriod?.toFixed(1) ?? '0.0';
            const inCart = validation.cart?.totalDFE?.toFixed(1) ?? '0.0';
            addWarningBanner(`<strong>Prescription limit exceeded</strong>. You have ${remaining}g remaining but your cart contains ${inCart}g.`);
          }
          if (validation.limits && validation.limits.isWithinTHCCap === false) {
            addWarningBanner('<strong>THC cap exceeded</strong>. One or more items exceed your prescribed THC level. Please remove those items.');
          }
        }
      }

      // Category limit details are rendered by the category limits banner inside the status block.
      
      // Guard against missing period/cart data (can happen on API errors)
      if (!validation.period || !validation.cart) {
        console.warn('[RecoverCann] Incomplete validation data, skipping render', {
          hasPeriod: Boolean(validation?.period),
          hasCart: Boolean(validation?.cart),
          superShopper: validation?.superShopper || null,
          errors: validation?.errors || null,
        });
        this.setStatusLoading(false);
        this.setCheckoutLoading(false);
        if (this.elements.loading) {
          this.elements.loading.style.display = 'none';
        }

        // Business-rule responses (e.g., expired prescription) may intentionally omit period/cart.
        // Keep status visible and show the API-provided message instead of a generic load error.
        if (validation.errors && validation.errors.length > 0) {
          if (this.elements?.error) {
            this.elements.error.style.display = 'none';
          }
          if (this.elements?.content) {
            this.elements.content.style.display = 'block';
            this.elements.content.dataset.loaded = 'true';
          }
          if (this.elements?.remaining) this.elements.remaining.textContent = '--';
          if (this.elements?.inCart) this.elements.inCart.textContent = '--';
          if (this.elements?.periodInfo) {
            const escaped = validation.errors[0].replace(/</g, '&lt;').replace(/>/g, '&gt;');
            this.elements.periodInfo.innerHTML = `${escaped}`;
          }
          if (this.elements?.largeWarning) this.elements.largeWarning.style.display = 'none';
          this.renderCategoryLimits([]);
          this.syncAllContainers();
          return;
        }

        if (this.elements?.error) {
          this.elements.error.style.display = 'block';
          const errorText = this.elements.error.querySelector('p');
          if (errorText) {
            errorText.textContent = 'Unable to check prescription limits. Please refresh.';
          }
        }
        return;
      }
      
      // Update values
      const remaining = validation.period.remainingInPeriod?.toFixed(1) ?? '0.0';
      const inCart = validation.cart.totalDFE?.toFixed(1) ?? '0.0';
      
      this.elements.remaining.textContent = `${remaining}g`;
      this.elements.inCart.textContent = `${inCart}g`;
      
      // Update colors
      if (validation.limits.isWithinPeriodLimit) {
        this.elements.inCart.classList.remove('recovercann-value-alert');
        this.elements.inCart.classList.add('recovercann-value-success');
      } else {
        this.elements.inCart.classList.remove('recovercann-value-success');
        this.elements.inCart.classList.add('recovercann-value-alert');
      }
      
      // Update progress bar
      this.updateProgressBar(
        validation.cart.totalDFE,
        validation.period.remainingInPeriod,
        validation.limits.isWithinPeriodLimit
      );
      
      // Update period info
      const firstOrder = validation.prescription?.firstOrder;
      const expiryDateRaw = validation.prescription?.expiry;
            // Use period.allotment (thc_limit/monthly_limit + prorated special_limit), not
      // prescription.monthlyLimit alone, so special authorization is reflected here too. Jay Grant
      const monthlyLimit = Number(validation.period?.allotment ?? validation.prescription?.monthlyLimit ?? 0);

      const formatShortDate = (value) => {
        if (!value) return null;
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };

      if (!firstOrder) {
        const expiryShortDate = formatShortDate(expiryDateRaw);
        const expiryMessage = expiryShortDate
          ? `Your prescription expires on ${expiryShortDate}.`
          : 'Your prescription expiry date is on file.';

        this.elements.periodInfo.innerHTML = `
          ${expiryMessage}
          <a href="/account">More info</a>
        `;
      } else {
        const endDateShort = formatShortDate(validation.period.endDate);
        const resetMessage = endDateShort
          ? `Your monthly allotment resets to ${monthlyLimit.toFixed(1)}g on ${endDateShort}.`
          : `Your monthly allotment resets to ${monthlyLimit.toFixed(1)}g.`;

        this.elements.periodInfo.innerHTML = `
          ${resetMessage}
          <a href="/account">More info</a>
        `;
      }
      
      // Mark content as loaded to reveal values via CSS
      this.elements.content.dataset.loaded = 'true';
      this.setStatusLoading(false);
      if (this.elements.loading) {
        this.elements.loading.style.display = 'none';
      }
      if (this.elements.error) {
        this.elements.error.style.display = 'none';
      }
      if (this.elements.content) {
        this.elements.content.style.display = 'block';
      }

      
      // Show large allotment warning
      this.elements.largeWarning.style.display =
        monthlyLimit > 150 ? 'flex' : 'none';
      
      // Render category limits if present (from limits.categoryLimits array)
      log('Category limits data:', validation.limits?.categoryLimits);
      this.renderCategoryLimits(validation.limits?.categoryLimits);
      
      // Sync all secondary containers (cart page + drawer share the same snippet)
      this.syncAllContainers();
      log('renderValidation:complete', getValidatorDebugState(this, 'render:complete'));
    }
    
    // Mirror rendered content from the primary container to all other instances
    // (the cart-status snippet is rendered in both the cart drawer and the cart page)
    syncAllContainers() {
      if (!this.container) return;
      const allContainers = getCartStatusContainers();
      if (allContainers.length <= 1) return;
      
      allContainers.forEach(secondary => {
        if (secondary === this.container) return;
        
        // Sync the main content area
        const primaryContent = this.container.querySelector('.recovercann-content');
        const secondaryContent = secondary.querySelector('.recovercann-content');
        if (primaryContent && secondaryContent) {
          secondaryContent.innerHTML = primaryContent.innerHTML;
          secondaryContent.dataset.loaded = primaryContent.dataset.loaded;
          secondaryContent.dataset.loading = primaryContent.dataset.loading || 'false';
        }
        
        // Sync warnings
        const primaryWarnings = this.container.querySelector('.recovercann-warnings');
        const secondaryWarnings = secondary.querySelector('.recovercann-warnings');
        if (primaryWarnings && secondaryWarnings) {
          secondaryWarnings.innerHTML = primaryWarnings.innerHTML;
        }
        
        // Sync loading state
        secondary.classList.toggle('is-loading', this.container.classList.contains('is-loading'));
        
        // Sync Super Shopper banner
        const primaryBanner = this.container.querySelector('.recovercann-super-shopper-banner');
        const existingBanner = secondary.querySelector('.recovercann-super-shopper-banner');
        if (primaryBanner && !existingBanner) {
          secondary.insertBefore(primaryBanner.cloneNode(true), secondary.firstChild);
        } else if (primaryBanner && existingBanner) {
          existingBanner.innerHTML = primaryBanner.innerHTML;
        } else if (!primaryBanner && existingBanner) {
          existingBanner.remove();
        }
        
        // Sync error state
        const primaryError = this.container.querySelector('.recovercann-error');
        const secondaryError = secondary.querySelector('.recovercann-error');
        if (primaryError && secondaryError) {
          secondaryError.style.display = primaryError.style.display;
          secondaryError.innerHTML = primaryError.innerHTML;
        }
      });
    }
    
    renderCategoryLimits(categoryLimits) {
      if (!this.elements.categoryLimits) {
        log('Category limits element not found');
        return;
      }

      // Ensure element is displayed so CSS transitions can work
      if (this.elements.categoryLimits.style.display === 'none') {
        this.elements.categoryLimits.style.display = 'block';
      }
      
      // Check if there are any category limits to display (it's an array)
      if (!categoryLimits || !Array.isArray(categoryLimits) || categoryLimits.length === 0) {
        this.elements.categoryLimits.classList.remove('is-visible');
        this.elements.categoryLimits.setAttribute('aria-hidden', 'true');
        this._lastCategoryHtml = null;
        return;
      }
      
      // Filter to only show categories where items are in cart AND limits exceeded
      const exceededLimits = categoryLimits.filter(limit => limit.inCart > 0 && limit.exceeds);
      
      if (exceededLimits.length === 0) {
        this.elements.categoryLimits.classList.remove('is-visible');
        this.elements.categoryLimits.setAttribute('aria-hidden', 'true');
        this._lastCategoryHtml = null;
        return;
      }
      
      // Build HTML and skip re-render if content hasn't changed (prevents flashing)
      const lines = exceededLimits.map(limit => {
        const totalUsed = limit.used + limit.inCart;
        return `<p style="margin: 2px 0 0;">${limit.title}: ${totalUsed}/${limit.limit} (limit: ${limit.limit} per 30 days)</p>`;
      }).join('');
      
      const html = `
        <div class="recovercann-category-exceeded-banner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="recovercann-category-exceeded-icon">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <div class="recovercann-category-exceeded-text">
            <p style="margin: 0;"><strong>Category limits exceeded:</strong></p>
            ${lines}
          </div>
        </div>
      `;
      
      // Only touch the DOM if something actually changed
      if (this.elements.categoryLimits.classList.contains('is-visible') &&
          this._lastCategoryHtml === html) {
        return;
      }
      
      this._lastCategoryHtml = html;
      this.elements.categoryLimits.innerHTML = html;
      this.elements.categoryLimits.classList.add('is-visible');
      this.elements.categoryLimits.setAttribute('aria-hidden', 'false');
    }
    
    updateProgressBar(inCart, remaining, isWithinLimit) {
      if (!this.elements?.bar || !this.elements?.marker) return;
      let percentage = 0;
      if (remaining > 0) {
        percentage = Math.min((inCart / remaining) * 100, 100);
      }
      
      this.elements.bar.style.width = `${percentage}%`;
      
      if (isWithinLimit) {
        this.elements.bar.classList.remove('recovercann-bar-alert');
        this.elements.bar.classList.add('recovercann-bar-success');
      } else {
        this.elements.bar.classList.remove('recovercann-bar-success');
        this.elements.bar.classList.add('recovercann-bar-alert');
      }
      
      // Position 150g marker
      if (remaining > 0) {
        const markerPercentage = Math.min((150 / remaining) * 100, 100);
        this.elements.marker.style.left = `${markerPercentage}%`;
        this.elements.marker.style.display = 'block';
      } else {
        this.elements.marker.style.display = 'none';
      }
    }

    resolveCartItemCount(validation = null) {
      const validationCount = Number(validation?.cart?.itemCount);
      if (Number.isFinite(validationCount) && validationCount >= 0) {
        return validationCount;
      }

      const cachedValidationCount = Number(validationCache.data?.cart?.itemCount);
      if (Number.isFinite(cachedValidationCount) && cachedValidationCount >= 0) {
        return cachedValidationCount;
      }

      const freshCartCount = Number(this.lastCartData?.item_count);
      if (Number.isFinite(freshCartCount) && freshCartCount >= 0) {
        return freshCartCount;
      }

      if (Array.isArray(this.lastCartData?.items)) {
        return this.lastCartData.items.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
      }

      const quantityInputs = Array.from(document.querySelectorAll('cart-quantity-selector-component input[name="updates[]"]'));
      if (quantityInputs.length > 0) {
        return quantityInputs.reduce((sum, input) => {
          const value = Number(input.value);
          return sum + (Number.isFinite(value) ? Math.max(value, 0) : 0);
        }, 0);
      }

      return 0;
    }

    hasItemsInCart(validation = null) {
      return this.resolveCartItemCount(validation) > 0;
    }
    
    updateCheckoutButton(canCheckout, isLoading = false) {
      const statusLoadingActive = Boolean(this._statusLoadingTimer) || Boolean(this.container?.classList?.contains('is-loading'));
      if (!isLoading && statusLoadingActive) {
        isLoading = true;
      }
      const effectiveCanCheckout = Boolean(canCheckout) && this.hasItemsInCart();
      setCheckoutGuardState({ canCheckout: effectiveCanCheckout, isLoading, initialized: true });

      // Skip if state hasn't changed (prevents visual flicker)
      const stateKey = `${effectiveCanCheckout}:${isLoading}`;
      if (this._lastButtonState === stateKey) return;
      this._lastButtonState = stateKey;
      
      const selectors = [
        '[name="checkout"]',
        '.cart__checkout-button',
        '[href*="/checkout"]',
        'button[type="submit"].cart-drawer__checkout'
      ];
      
      const buttons = document.querySelectorAll(selectors.join(', '));
      
      buttons.forEach(button => {
        const shouldDisable = isLoading || !effectiveCanCheckout;

        // Handle loading state
        if (isLoading) {
          button.classList.add('recovercann-btn-loading');
        } else {
          button.classList.remove('recovercann-btn-loading');
        }

        if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
          button.disabled = shouldDisable;
        } else {
          if (shouldDisable) {
            button.setAttribute('tabindex', '-1');
            button.dataset.recovercannDisabled = 'true';
          } else {
            button.removeAttribute('tabindex');
            delete button.dataset.recovercannDisabled;
          }
        }
        
        button.classList.toggle('disabled', shouldDisable && !isLoading);
        button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      });

      const acceleratedContainers = document.querySelectorAll(
        '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
      );

      acceleratedContainers.forEach((container) => {
        if (isLoading || !effectiveCanCheckout) {
          container.style.pointerEvents = 'none';
          container.style.opacity = '0.5';
          container.setAttribute('aria-hidden', 'true');
        } else {
          container.style.pointerEvents = '';
          container.style.opacity = '';
          container.setAttribute('aria-hidden', 'false');
        }
      });
    }
    
    // Show checkout button loading state - preserves actual canCheckout from cache
    setCheckoutLoading(isLoading) {
      if (isLoading && this.container && this.elements?.content) {
        if (this._statusLoadingTimer) {
          clearTimeout(this._statusLoadingTimer);
          this._statusLoadingTimer = null;
        }

        const statusLoadingActive = this.container.classList.contains('is-loading');
        if (!statusLoadingActive) {
          this.container.classList.add('is-loading');
          this.elements.content.dataset.loading = 'true';
          if (this.elements.loading) {
            this.elements.loading.style.display = 'flex';
          }
          if (this.elements.error) {
            this.elements.error.style.display = 'none';
          }
        }
      }

      const canCheckout = validationCache.data?.canCheckout ?? false;
      this.updateCheckoutButton(canCheckout, isLoading);
    }
    
    renderSuperShopperBanner(superShopper) {
      // Check if banner already exists
      let banner = document.getElementById('super-shopper-banner');
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'super-shopper-banner';
        banner.className = 'recovercann-super-shopper-banner';
        this.container.insertBefore(banner, this.container.firstChild);
      }
      
      banner.innerHTML = `
        <div class="super-shopper-banner-content">
          <p class="super-shopper-message">
            <strong>SuperShopper ${superShopper.staffFirstName} ${superShopper.staffLastName}.</strong>
            You are viewing the store as ${superShopper.customerFirstName} ${superShopper.customerLastName}
          </p>
        </div>
      `;
    }

    maybeAutoDisableSuperShopper(validation) {
      if (!validation?.superShopper?.enabled) return false;
      const sessionTarget = safeSessionGet('superShopperCustomerId');
      if (sessionTarget) return false;

      // The backend just confirmed Super Shopper IS active via the customer's
      // Shopify metafields (the real, account-wide, persistent state) — a
      // missing sessionStorage value here is a fragile client-side cache
      // gap (a new tab, a reload timing gap), not evidence the staffer
      // wants out. Previously this called the real disable endpoint in
      // this case, which flips the metafield off for the whole account and
      // kicks the staffer out of an otherwise-valid session mid-cart. Resync
      // the local cache from the trusted response instead of tearing down
      // a session that's actually still valid.
      const sp = validation.superShopper;
      if (sp.customerId) {
        safeSessionSet('superShopperCustomerId', sp.customerId);
        safeSessionSet('superShopperCustomerName', `${sp.customerFirstName || ''} ${sp.customerLastName || ''}`.trim());
      }
      return false;
    }
    
    replaceCheckoutWithDraftOrder(validation) {
      // Store latest validation so we can re-apply after morph
      this._lastSuperShopperValidation = validation;

      const selectors = [
        '[name="checkout"]',
        '.cart__checkout-button',
        '[href*="/checkout"]',
        'button[type="submit"].cart-drawer__checkout'
      ];
      
      // Check if cart is empty - handle both validation object and fallback
      const cartItemCount = validation?.cart?.itemCount ?? 0;
      const cartIsEmpty = cartItemCount === 0;
      
      document.querySelectorAll(selectors.join(', ')).forEach(button => {
        // Always hide the original checkout button
        button.style.display = 'none';
        button.dataset.replacedWithDraft = 'true';
        
        // Check if a draft button already exists as the next sibling
        const existingDraft = button.nextElementSibling?.classList?.contains('recovercann-draft-order-button')
          ? button.nextElementSibling : null;
        
        if (existingDraft) {
          // Draft button exists — just update disabled state
          existingDraft.disabled = cartIsEmpty;
          return;
        }
        
        // Create draft order button
        const draftButton = document.createElement('button');
        draftButton.type = 'button';
        draftButton.className = button.className + ' recovercann-draft-order-button';
        draftButton.textContent = 'Create Draft Order';
        draftButton.disabled = cartIsEmpty;
        
        draftButton.addEventListener('click', () => this.createDraftOrder(validation));
        
        // Insert after original button
        button.parentNode.insertBefore(draftButton, button.nextSibling);
      });
    }
    
    restoreCheckoutButton() {
      // Clear cached validation so cart:update won't re-insert
      this._lastSuperShopperValidation = null;

      // Remove draft order buttons and show original checkout buttons
      document.querySelectorAll('.recovercann-draft-order-button').forEach(btn => {
        btn.remove();
      });
      
      const selectors = [
        '[name="checkout"]',
        '.cart__checkout-button',
        '[href*="/checkout"]',
        'button[type="submit"].cart-drawer__checkout'
      ];
      
      document.querySelectorAll(selectors.join(', ')).forEach(button => {
        if (button.dataset.replacedWithDraft) {
          button.style.display = '';
          delete button.dataset.replacedWithDraft;
        }
      });
      
      // Remove Super Shopper banner from cart if present
      const banner = document.getElementById('super-shopper-banner');
      if (banner) {
        banner.remove();
      }
    }
    
    async createDraftOrder(validation) {
      const draftButtons = document.querySelectorAll('.recovercann-draft-order-button');
      draftButtons.forEach(btn => {
        btn.disabled = true;
        btn.textContent = 'Creating...';
      });
      
      try {
        const cartData = await this.fetchCart();
        if (!cartData) {
          throw new Error('Unable to load cart. Please check your connection and try again.');
        }
        const cartItems = cartData.items.map(item => ({
          variantId: item.variant_id.toString(),
          quantity: item.quantity
        }));
        
        // Extract staff and target customer IDs
        // Use sessionStorage as fallback since API might not return superShopper info
        const sessionTargetId = safeSessionGet('superShopperCustomerId');
        const staffCustomerId = normalizeCustomerId(validation?.superShopper?.staffId || this.customerId);
        const normalizedTargetId = normalizeCustomerId(
          validation?.superShopper?.customerId || sessionTargetId || this.customerId
        );

        if (!staffCustomerId || !normalizedTargetId) {
          throw new Error('Missing staff or target customer ID');
        }
        
        const response = await fetch(`${API_BASE}/api/staff/create-draft-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: normalizedTargetId, staffCustomerId, cartItems })
        });
        
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          const message = errorPayload?.error || 'Failed to create draft order';
          throw new Error(message);
        }
        
        const result = await response.json();
        
        // Show success message
        alert(`Success! ${result.message}\n\nDraft order ${result.draftOrder.name} has been created.`);
        
        // Clear cart
        await fetch('/cart/clear.js', { method: 'POST' });
        
        // Reload page
        window.location.reload();
        
      } catch (error) {
        console.error('Draft order error:', error);
        const message = error?.message || 'Failed to create draft order. Please try again.';
        alert(message);
        
        draftButtons.forEach(btn => {
          btn.disabled = false;
          btn.textContent = 'Create Draft Order';
        });
      }
    }
    
    setupCartListeners() {
      // Listen for theme's cart update event (ThemeEvents.cartUpdate = 'cart:update')
      document.addEventListener('cart:update', (event) => {
        this._lastCartUpdateEventTs = Date.now();
        log('cart:update event', {
          ...getValidatorDebugState(this, 'cart:update'),
          sourceId: event.detail?.sourceId || null,
          forceRefresh: Boolean(event.detail?.forceRefresh),
          hasSections: Boolean(event.detail?.data?.sections),
        });
        // Skip if we're the source to avoid loops
        if (event.detail?.sourceId === 'recovercann-validation') return;
        
        if (event.detail?.forceRefresh) {
          log(' Force refresh triggered');
          this.scheduleRefresh(true);
          return;
        }

        // After Shopify's section rendering / morph, the dynamically inserted
        // draft-order button gets removed.  Re-apply it from cached state.
        if (this.isSuperShopperActive && this._lastSuperShopperValidation) {
          requestAnimationFrame(() => {
            this.replaceCheckoutWithDraftOrder(this._lastSuperShopperValidation);
          });
        }

        if (STABLE_UI_MODE) {
          // In stable mode, still run a silent refresh after native cart:update
          // so the drawer reflects the latest cart immediately on first flyout.
          this.scheduleRefresh(true, { showLoading: false });
          return;
        }
        
        // Re-render from cache after section rendering completes.
        // morph skips our content (data-skip-subtree-update on #recovercann-cart-status)
        // and our button (data-skip-node-update on #checkout), so this is a safety net
        // to ensure display is current after any cart change.
        this.refreshElements();
        if (validationCache.data) {
          this.renderValidation(validationCache.data, true);
          this.updateCheckoutButton(validationCache.data.canCheckout);
        } else {
          this.refresh();
        }
      });
      
      // OPTIMIZATION: Wrap fetch to intercept cart changes BEFORE they complete
      if (!window._recovercannFetchWrapped) {
        window._recovercannFetchWrapped = true;
        const originalFetch = window.fetch;
        const validator = this;

        const getRequestUrl = (input) => {
          if (typeof input === 'string') return input;
          if (input && typeof input.url === 'string') return input.url;
          return '';
        };
        
        window.fetch = async (...args) => {
          const url = args[0];
          const requestUrl = getRequestUrl(url);
          const options = args[1];
          
          // Intercept cart/change requests to calculate optimistically
          if (requestUrl.includes('/cart/change') && options?.body) {
            try {
              const body = JSON.parse(options.body);
              if (body.line && body.quantity !== undefined) {
                if (!STABLE_UI_MODE && !validator.isSuperShopperActive) {
                  validator.optimisticUpdate(body.line, body.quantity);
                }
              }
            } catch (e) { /* ignore parse errors */ }
            validator.setCheckoutLoading(true);
          }
          
          // Capture generation AFTER optimistic update so we can detect stale responses
          const fetchGeneration = validator._generation;
          
          const response = await originalFetch(...args);
          
          if (requestUrl && (
            requestUrl.includes('/cart/add') ||
            requestUrl.includes('/cart/update') ||
            requestUrl.includes('/cart/change') ||
            requestUrl.includes('/cart/clear')
          )) {
            if (requestUrl.includes('/cart/change')) {
              // Only process if this is still the latest generation
              // (no newer optimistic update has occurred since this fetch started)
              response.clone().json().then(cartData => {
                if (validator.isSuperShopperActive) {
                  validator.lastCartData = cartData;
                  validator.updateSubtotal(cartData);
                  validator.scheduleRefresh(true);
                  return;
                }
                if (fetchGeneration < validator._generation) {
                  log(' Ignoring stale fetch response (gen', fetchGeneration, '< current', validator._generation, ')');
                  return;
                }
                validator.lastCartData = cartData;
                validator.updateSubtotal(cartData);
                if (STABLE_UI_MODE) {
                  validator.scheduleRefresh(false);
                  return;
                }
                if (!STABLE_UI_MODE) {
                  // Background API refresh for accurate category limits (2s delay)
                  clearTimeout(validator._apiRefreshTimer);
                  validator._apiRefreshTimer = setTimeout(() => {
                    // Use fresh cart data, not the potentially stale response
                    validator.scheduleRefresh(true);
                  }, 2000);
                }
              }).catch(() => {});
            } else {
              // For add/clear or other changes, do a full refresh
              validator.scheduleRefresh(true);
            }

            if (requestUrl.includes('/cart/add') && response.ok) {
              validator.scheduleCartUpdateBridge('cart-add-fetch');
            }
          }
          
          return response;
        };
      }
    }

    scheduleCartUpdateBridge(reason = 'cart-add') {
      if (this._cartUpdateBridgeTimer) {
        clearTimeout(this._cartUpdateBridgeTimer);
      }

      this._cartUpdateBridgeTimer = setTimeout(async () => {
        const msSinceNativeEvent = Date.now() - (this._lastCartUpdateEventTs || 0);
        if (msSinceNativeEvent < 300) {
          log('Bridge skipped (native cart:update observed)', { reason, msSinceNativeEvent });
          return;
        }

        let cartData = null;
        try {
          cartData = await fetchFreshCart();
          this.lastCartData = cartData;
        } catch (error) {
          log('Bridge cart fetch failed', { reason, error: error?.message || error });
        }

        log('Dispatching fallback cart:update bridge', {
          reason,
          msSinceNativeEvent,
          itemCount: cartData?.item_count ?? null,
        });

        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            resource: cartData || {},
            sourceId: 'recovercann-fetch-bridge',
            forceRefresh: true,
            data: {
              source: 'recovercann-fetch-bridge',
              itemCount: cartData?.item_count ?? null,
            },
          },
        }));
      }, 180);
    }
    
    // OPTIMISTIC UPDATE: Update cache + render immediately before Shopify responds.
    // morph won't undo our changes thanks to data-skip-subtree-update on
    // #recovercann-cart-status and data-skip-node-update on #checkout button.
    optimisticUpdate(lineNumber, newQuantity) {
      if (STABLE_UI_MODE) return;
      if (this.isSuperShopperActive) return;
      if (!this.lastCartData || !dfeCache.initialized) return;
      
      // Increment generation — all in-flight fetch responses with older generations
      // will be ignored when they arrive
      this._generation++;
      
      const items = [...this.lastCartData.items];
      const itemIndex = lineNumber - 1;
      
      if (itemIndex < 0 || itemIndex >= items.length) return;
      
      const item = items[itemIndex];
      
      if (newQuantity === 0) {
        items.splice(itemIndex, 1);
      } else {
        items[itemIndex] = { ...item, quantity: newQuantity };
      }
      
      const fakeCartData = { items };
      if (this.canRecalculateLocally(fakeCartData)) {
        const localValidation = this.recalculateLocally(fakeCartData);
        
        validationCache.data = localValidation;
        const optimisticHash = hashCart({ items }, this.activeCustomerId || this.customerId);
        validationCache.cartHash = optimisticHash;
        validationCache.timestamp = Date.now();
        
        // Render immediately — morph preserves our DOM
        this.refreshElements();
        this.renderValidation(localValidation, true);
        this.updateCheckoutButton(localValidation.canCheckout);
        
        // Update subtotal optimistically
        const projectedSubtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
        if (this.elements.subtotal) {
          this.elements.subtotal.textContent = this.formatMoney(projectedSubtotal);
        }
        
        log(' Optimistic update rendered immediately');
      }
    }
    
    showError(message) {
      if (!this.container || !this.elements?.content) {
        this.refreshElements();
      }
      if (!this.elements?.loading || !this.elements?.content || !this.elements?.error) return;
      this.elements.loading.style.display = 'none';
      this.elements.content.style.display = 'none';
      this.elements.error.style.display = 'block';
      const errorText = this.elements.error.querySelector('p');
      if (errorText) {
        errorText.textContent = message;
      }
      // Disable checkout when validation fails to prevent unvalidated purchases
      this.updateCheckoutButton(false);
      // Sync error state to secondary containers
      this.syncAllContainers();
    }
  }
  
  // ===== PRODUCT VALIDATION =====
  class ProductValidator {
    constructor() {
      this.container = document.getElementById('recovercann-product-validator');
      if (!this.container) return;
      this.formComponent = this.container.closest('product-form-component');
      
      this.customerId = this.container.dataset.customerId;
      this.productId = this.container.dataset.productId;
      this.variantDeductions = this.parseVariantDeductions(this.container.dataset.variantDeductions);
      this.variantInventory = this.parseVariantInventory(this.container.dataset.variantInventory);
      this.variantPrices = this.parseVariantPrices(this.container.dataset.variantPrices);
      this.lastCartQuantity = 0;
      this.lastVariantId = null;
      this.currentVariantId = null;
      this.stockHintTimeout = null;
      
      if (!this.customerId || !this.productId) return;
      
      this.elements = {
        warning: this.container.querySelector('.recovercann-product-warning'),
        warningText: this.container.querySelector('.recovercann-product-warning-text'),
        info: this.container.querySelector('.recovercann-product-info'),
        infoText: this.container.querySelector('.recovercann-product-info-text'),
        categoryInfo: this.container.querySelector('.recovercann-product-category-info'),
        categoryInfoContent: this.container.querySelector('.recovercann-product-category-info-content'),
        categoryWarning: this.container.querySelector('.recovercann-product-category-warning'),
        categoryWarningText: this.container.querySelector('.recovercann-product-category-warning-text'),
        stockWarning: this.container.querySelector('.recovercann-product-stock-warning'),
        stockWarningText: this.container.querySelector('.recovercann-product-stock-warning-text'),
        stockHint: this.container.querySelector('.recovercann-product-stock-hint'),
        stockHintText: this.container.querySelector('.recovercann-product-stock-hint-text'),
        deduction: this.container.querySelector('.recovercann-product-deduction'),
        deductionValue: this.container.querySelector('.recovercann-product-deduction-value'),
      };
      
      this.init();
    }
    
    async init() {
      this.setAddToCartLoading(true);

      const cart = await this.fetchCart();
      if (!cart) {
        this.setAddToCartLoading(false);
        return;
      }
      
      // Get selected variant
      const variantId = this.currentVariantId || this.getSelectedVariantId();
      
      if (!variantId) {
        this.setAddToCartLoading(false);
        return;
      }
      
      // Get the current quantity selector value (default 1)
      const qtyInput = this.getQuantityInput();
      const selectedQuantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      this.updateAddToCartPriceDisplay(selectedQuantity, variantId);
      
      // Simulate adding this product with selected quantity
      const testCart = {
        customerId: `gid://shopify/Customer/${this.customerId}`,
        cartItems: [
          ...cart.items.map(item => ({
            variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
            productId: `gid://shopify/Product/${item.product_id}`,
            quantity: item.quantity
          })),
          {
            variantId: `gid://shopify/ProductVariant/${variantId}`,
            productId: `gid://shopify/Product/${this.productId}`,
            quantity: selectedQuantity
          }
        ]
      };
      
      const cartItem = cart.items.find(item => this.normalizeVariantId(item.variant_id) === this.normalizeVariantId(variantId));
      const cartQuantity = cartItem ? cartItem.quantity : 0;
      this.lastCartQuantity = cartQuantity;
      this.lastVariantId = this.normalizeVariantId(variantId);

      const validation = await this.validate(testCart);
      if (validation) {
        this.updateUI(validation, selectedQuantity, variantId, cartQuantity);
      } else {
        this.setAddToCartLoading(false);
      }
      
      this.setupVariantListener();
      this.setupQuantityListener();
      this.setupCartListener();
    }
    
    async fetchCart() {
      try {
        return await fetchFreshCart();
      } catch (error) {
        console.error('Cart fetch error:', error);
        return null;
      }
    }
    
    async validate(testCart) {
      try {
        const response = await postCartValidation(testCart);
        
        if (!response.ok) throw new Error('Validation failed');
        return await response.json();
      } catch (error) {
        console.error('Validation error:', error);
        return null;
      }
    }
    
    /**
     * Find category limits relevant to the current product
     */
    getProductCategoryLimits(categoryLimits) {
      if (!categoryLimits || !Array.isArray(categoryLimits)) return [];
      const productGid = `gid://shopify/Product/${this.productId}`;
      // The API returns categories where items are in cart — since we simulated adding this product,
      // any category containing this product will appear with inCart > 0
      return categoryLimits.filter(limit => limit.inCart > 0);
    }
    
    updateUI(validation, selectedQuantity, variantId, cartQuantity) {
      const scope = this.formComponent || this.container.closest('shopify-section, dialog, product-card') || document;
      const addToCartButtons = scope.querySelectorAll(
        '[name="add"], .product-form__submit, button[type="submit"]'
      );

      const stockValidation = this.validateStockForSelection(variantId, cartQuantity, selectedQuantity);

      this.updateAddToCartPriceDisplay(selectedQuantity, variantId);
      this.updateDeductionDisplay(selectedQuantity, variantId);
      this.updateStockWarning(cartQuantity, variantId, selectedQuantity, stockValidation);
      
      // ── Category Limits ──
      const categoryLimits = validation.limits?.categoryLimits || [];
      const productCategoryLimits = this.getProductCategoryLimits(categoryLimits);
      const exceededCategoryLimits = productCategoryLimits.filter(l => l.exceeds);
      const hasCategoryLimitViolation = exceededCategoryLimits.length > 0;
      
      // Show category limit info for products in limited collections (always, even if within limit)
      if (productCategoryLimits.length > 0 && this.elements.categoryInfo && this.elements.categoryInfoContent) {
        if (!hasCategoryLimitViolation) {
          // Within limit — show informational blue box
          this.elements.categoryInfo.style.display = 'flex';
          this.elements.categoryInfoContent.innerHTML = productCategoryLimits.map(limit => {
            const totalUsed = limit.used + limit.inCart;
            return `<p class="recovercann-product-category-line"><strong>Limit:</strong> ${limit.limit} ${limit.title} per order cycle</p>` +
                   `<p class="recovercann-product-category-line"><strong>Used:</strong> ${totalUsed} / ${limit.limit}${limit.used > 0 ? ` (${limit.used} from previous orders)` : ''}</p>`;
          }).join('');
        } else {
          this.elements.categoryInfo.style.display = 'none';
        }
      } else if (this.elements.categoryInfo) {
        this.elements.categoryInfo.style.display = 'none';
      }
      
      // Show category limit exceeded warning
      if (hasCategoryLimitViolation && this.elements.categoryWarning && this.elements.categoryWarningText) {
        this.elements.categoryWarning.style.display = 'flex';
        this.elements.categoryWarningText.innerHTML = exceededCategoryLimits.map(limit => {
          const totalUsed = limit.used + limit.inCart;
          return `${limit.title}: ${totalUsed} / ${limit.limit} (exceeds limit of ${limit.limit} per order cycle). Reduce quantity to add to cart.`;
        }).join('<br>');
      } else if (this.elements.categoryWarning) {
        this.elements.categoryWarning.style.display = 'none';
      }
      
      // ── Prescription / Generic Limits ──
      const canCheckoutAndInStock = validation.canCheckout && stockValidation.canAdd;
      const hasGenericError = !canCheckoutAndInStock && !hasCategoryLimitViolation && !stockValidation.isStockError;
      
      if (!canCheckoutAndInStock) {
        // Show generic prescription warning only if category limits aren't the sole cause
        if (hasGenericError) {
          this.elements.warning.style.display = 'flex';
          const errorMessage = validation.errors ? 
            validation.errors.filter(e => !categoryLimits.some(l => e.includes(l.title))).join(' ') || validation.errors.join(' ')
            : 'Adding this item would exceed your prescription limits.';
          this.elements.warningText.textContent = errorMessage;
        } else {
          this.elements.warning.style.display = 'none';
        }
        this.elements.info.style.display = 'none';
        
        // Disable add-to-cart buttons
        addToCartButtons.forEach(button => {
          button.disabled = true;
          button.classList.add('disabled');
          button.setAttribute('aria-disabled', 'true');
        });
      } else {
        // Within all limits — keep info box hidden
        this.elements.warning.style.display = 'none';
        this.elements.info.style.display = 'none';
        
        // Enable add-to-cart buttons
        addToCartButtons.forEach(button => {
          const isRecoverCannGuarded = button.dataset?.recovercannAwaitValidation === 'true';
          if (isRecoverCannGuarded) {
            button.disabled = false;
            button.classList.remove('disabled');
            button.removeAttribute('aria-disabled');
          }
        });
      }

      this.setAddToCartLoading(false);
    }

    validateStockForSelection(variantId, cartQuantity, selectedQuantity) {
      if (!variantId) {
        return {
          canAdd: false,
          isStockError: true,
          reason: 'unavailable',
          message: 'This variant is unavailable.',
        };
      }

      const normalizedVariantId = this.normalizeVariantId(variantId);
      const inventory = this.variantInventory.get(normalizedVariantId);
      if (!inventory) {
        return {
          canAdd: true,
          isStockError: false,
          reason: null,
          message: '',
        };
      }

      const isAvailable = inventory.available !== false;
      if (!isAvailable) {
        return {
          canAdd: false,
          isStockError: true,
          reason: 'unavailable',
          message: 'This variant is unavailable.',
        };
      }

      const hasManagedInventory = inventory.managed === 1;
      const qty = Number(inventory.qty || 0);
      const policy = inventory.policy || '';

      if (hasManagedInventory && policy === 'deny') {
        if (qty <= 0) {
          return {
            canAdd: false,
            isStockError: true,
            reason: 'sold_out',
            message: 'This item is sold out.',
          };
        }

        const requestedQuantity = Math.max(Number(selectedQuantity || 1), 1);
        if (cartQuantity + requestedQuantity > qty) {
          return {
            canAdd: false,
            isStockError: true,
            reason: 'insufficient',
            message: cartQuantity >= qty
              ? 'All available stock is already in your cart.'
              : `Only ${Math.max(qty - cartQuantity, 0)} left in stock.`,
          };
        }
      }

      return {
        canAdd: true,
        isStockError: false,
        reason: null,
        message: '',
      };
    }

    setAddToCartLoading(isLoading) {
      const scopedRoot = this.formComponent || this.container.closest('.shopify-section, dialog, product-card') || document;
      const guardedButtons = scopedRoot.querySelectorAll('[data-recovercann-await-validation="true"]');

      guardedButtons.forEach((button) => {
        if (isLoading) {
          button.disabled = true;
          button.classList.add('disabled');
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('recovercann-btn-loading');
        } else {
          button.classList.remove('recovercann-btn-loading');
        }
      });
    }

    parseVariantDeductions(rawJson) {
      if (!rawJson) return new Map();
      try {
        const list = JSON.parse(rawJson);
        if (!Array.isArray(list)) return new Map();
        return new Map(list.map(entry => [String(entry.id), Number(entry.dfe || 0)]));
      } catch (error) {
        console.warn('[RecoverCann] Failed to parse variant deductions', error);
        return new Map();
      }
    }

    parseVariantInventory(rawJson) {
      if (!rawJson) return new Map();
      try {
        const list = JSON.parse(rawJson);
        if (!Array.isArray(list)) return new Map();
        return new Map(list.map(entry => [
          String(entry.id),
          {
            qty: Number(entry.qty || 0),
            managed: Number(entry.managed || 0),
            policy: entry.policy || '',
            available: entry.available !== false && entry.available !== 'false' && entry.available !== 0,
          },
        ]));
      } catch (error) {
        console.warn('[RecoverCann] Failed to parse variant inventory', error);
        return new Map();
      }
    }

    parseVariantPrices(rawJson) {
      if (!rawJson) return new Map();
      try {
        const list = JSON.parse(rawJson);
        if (!Array.isArray(list)) return new Map();
        return new Map(list.map(entry => [String(entry.id), Number(entry.price || 0)]));
      } catch (error) {
        console.warn('[RecoverCann] Failed to parse variant prices', error);
        return new Map();
      }
    }

    parseMoneyTextToCents(value) {
      if (!value) return 0;
      const normalized = String(value).replace(/[^0-9.,-]/g, '').trim();
      if (!normalized) return 0;

      const hasComma = normalized.includes(',');
      const hasDot = normalized.includes('.');
      let decimalNormalized = normalized;

      if (hasComma && hasDot) {
        decimalNormalized = normalized.replace(/,/g, '');
      } else if (hasComma && !hasDot) {
        decimalNormalized = normalized.replace(',', '.');
      }

      const amount = Number.parseFloat(decimalNormalized);
      if (Number.isNaN(amount)) return 0;
      return Math.round(amount * 100);
    }

    formatCurrencyFromCents(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        const currencyCode = window?.Shopify?.currency?.active || window?.Shopify?.currency?.shop_currency || 'CAD';
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    getVariantUnitPrice(variantId, eventVariantPrice = 0) {
      const normalizedVariantId = this.normalizeVariantId(variantId);
      const fromMap = this.variantPrices.get(normalizedVariantId) || 0;
      if (fromMap > 0) return fromMap;

      const fromEvent = Number(eventVariantPrice || 0);
      if (fromEvent > 0) return fromEvent;

      const scope = this.formComponent || this.container.closest('.shopify-section, dialog, product-card') || document;
      const checkedVariantInput = scope.querySelector('.variant-picker input[type="radio"]:checked');
      if (checkedVariantInput instanceof HTMLInputElement) {
        const selectedLabel = checkedVariantInput.closest('label');
        const selectedPriceNode = selectedLabel?.querySelector('.variant-option__button-price');
        if (selectedPriceNode?.textContent) {
          const parsed = this.parseMoneyTextToCents(selectedPriceNode.textContent);
          if (parsed > 0) return parsed;
        }
      }

      return 0;
    }

    updateAddToCartPriceDisplay(selectedQuantity, variantId, eventVariantPrice = 0) {
      if (!variantId) return;

      const scope = this.formComponent || this.container.closest('.shopify-section, dialog, product-card') || document;
      const addButton = scope.querySelector('button[name="add"][data-recovercann-await-validation="true"], button[name="add"]');
      if (!addButton) return;

      const labelContainer = addButton.querySelector('.add-to-cart-text__content');
      if (!labelContainer) return;

      const unitPrice = this.getVariantUnitPrice(variantId, eventVariantPrice);
      if (unitPrice <= 0) return;

      const quantity = Math.max(Number(selectedQuantity || 1), 1);
      const total = unitPrice * quantity;
      // The unit price already shows above the sizes, so only spell out a total once it differs from it
      const label = quantity > 1 ? `Add ${quantity} to bag · ${this.formatCurrencyFromCents(total)}` : 'Add to bag';

      const nestedLabel = labelContainer.querySelector('span > span');
      if (nestedLabel) {
        nestedLabel.textContent = label;
      } else {
        labelContainer.textContent = label;
      }
    }

    updateDeductionDisplay(selectedQuantity, variantId) {
      if (!this.elements.deduction || !this.elements.deductionValue) return;
      if (!variantId) return;

      const normalizedVariantId = this.normalizeVariantId(variantId);
      const dfePerUnit = this.variantDeductions.get(normalizedVariantId) || 0;
      const total = dfePerUnit * (selectedQuantity || 1);

      if (dfePerUnit > 0) {
        this.elements.deduction.style.display = 'block';
        this.elements.deductionValue.textContent = `${total.toFixed(1)}g`;
      } else {
        this.elements.deduction.style.display = 'none';
      }
    }

    updateStockWarning(cartQuantity, variantId, selectedQuantity = 1, stockValidation = null) {
      if (!this.elements.stockWarning) return;
      if (!variantId) return;

      const stockState = stockValidation || this.validateStockForSelection(variantId, cartQuantity, selectedQuantity);
      if (stockState.isStockError) {
        if (this.elements.stockWarningText && stockState.message) {
          this.elements.stockWarningText.textContent = stockState.message;
        }
        this.elements.stockWarning.style.display = 'flex';
        return;
      }

      const normalizedVariantId = this.normalizeVariantId(variantId);
      const inventory = this.variantInventory.get(normalizedVariantId);
      if (!inventory) {
        this.elements.stockWarning.style.display = 'none';
        return;
      }

      const hasManagedInventory = inventory.managed === 1;
      const qty = Number(inventory.qty || 0);
      const policy = inventory.policy || '';
      const shouldShow = hasManagedInventory && policy === 'deny' && qty > 0 && cartQuantity >= qty;

      this.elements.stockWarning.style.display = shouldShow ? 'flex' : 'none';
    }

    showStockHint(variantId) {
      if (!this.elements.stockHint || !this.elements.stockHintText) return;
      if (!variantId) return;

      const normalizedVariantId = this.normalizeVariantId(variantId);
      const inventory = this.variantInventory.get(normalizedVariantId);
      if (!inventory || inventory.managed !== 1) {
        this.elements.stockHint.style.display = 'none';
        return;
      }

      const qty = Number(inventory.qty || 0);

      const remaining = Math.max(qty - (this.lastVariantId === normalizedVariantId ? this.lastCartQuantity : 0), 0);
      this.elements.stockHintText.textContent = `In stock: ${remaining}`;
      this.elements.stockHint.style.display = 'block';

      if (this.stockHintTimeout) {
        clearTimeout(this.stockHintTimeout);
      }
      this.stockHintTimeout = setTimeout(() => {
        this.elements.stockHint.style.display = 'none';
      }, 1500);
    }
    
    setupVariantListener() {
      // Guard against stacking listeners on repeated init() calls
      if (this._variantListenerBound) return;
      this._variantListenerBound = true;

      const target = this.container.closest('.shopify-section, dialog, product-card') || this.formComponent || document;
      target.addEventListener('variant:update', (event) => {
        this.setAddToCartLoading(true);

        const variantId = event?.detail?.resource?.id
          ? this.normalizeVariantId(event.detail.resource.id)
          : this.getSelectedVariantId();
        this.currentVariantId = variantId;
        const qtyInput = this.getQuantityInput();
        const selectedQuantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const eventVariantPrice = Number(event?.detail?.resource?.price || 0);

        this.updateAddToCartPriceDisplay(selectedQuantity, variantId, eventVariantPrice);

        if (variantId) {
          this.updateDeductionDisplay(selectedQuantity, variantId);
          this.showStockHint(variantId);
        }

        clearTimeout(this._variantDebounce);
        this._variantDebounce = setTimeout(() => this.init(), 120);
      });

      const variantInput = this.getVariantIdInput();
      if (variantInput) {
        variantInput.addEventListener('change', () => this.init());
      }
    }
    
    setupQuantityListener() {
      if (this._quantityListenerBound) return;
      this._quantityListenerBound = true;
      // Re-validate when quantity changes
      const qtyInputs = this.formComponent
        ? this.formComponent.querySelectorAll('input[name="quantity"], .quantity__input, [data-quantity-input]')
        : document.querySelectorAll('input[name="quantity"], .quantity__input, [data-quantity-input]');
      qtyInputs.forEach(input => {
        input.addEventListener('change', () => {
          const quantity = parseInt(input.value, 10) || 1;
          this.updateAddToCartPriceDisplay(quantity, this.getSelectedVariantId());
          this.showStockHint(this.getSelectedVariantId());
          this.init();
        });
        input.addEventListener('input', () => {
          clearTimeout(this._qtyDebounce);
          const quantity = parseInt(input.value, 10) || 1;
          this.updateAddToCartPriceDisplay(quantity, this.getSelectedVariantId());
          this.showStockHint(this.getSelectedVariantId());
          this._qtyDebounce = setTimeout(() => this.init(), 400);
        });
      });

      document.addEventListener('quantity-selector:update', (event) => {
        const quantity = event?.detail?.quantity;
        const cartLine = event?.detail?.cartLine;
        if (cartLine) return;
        if (typeof quantity !== 'number') return;

        const variantId = this.getSelectedVariantId();
        this.updateAddToCartPriceDisplay(quantity, variantId);
        this.updateDeductionDisplay(quantity, variantId);
        this.showStockHint(variantId);

        clearTimeout(this._qtyDebounce);
        this._qtyDebounce = setTimeout(() => this.init(), 200);
      });
    }

    setupCartListener() {
      if (this._cartListenerBound) return;
      this._cartListenerBound = true;

      document.addEventListener('cart:update', () => {
        // When the cart drawer is open the product-page test cart (existing items +
        // whatever quantity is selected on the page) is irrelevant to checkout
        // eligibility — the CartValidator handles that independently.  Running
        // init() here would add the page's selected quantity to the test, which
        // can push the simulated total over the limit and disable checkout even
        // though the actual cart is within limits.
        const cartDialog = document.querySelector('cart-drawer-component dialog');
        if (cartDialog && cartDialog.open) return;

        clearTimeout(this._cartDebounce);
        this._cartDebounce = setTimeout(() => this.init(), 200);
      });
    }

    getSelectedVariantId() {
      const variantInput = this.getVariantIdInput();
      return variantInput ? this.normalizeVariantId(variantInput.value) : null;
    }

    normalizeVariantId(variantId) {
      if (variantId == null) return '';
      const value = String(variantId);
      if (value.includes('/')) {
        return value.split('/').pop() || '';
      }
      return value;
    }

    getVariantIdInput() {
      if (this.formComponent) {
        const inFormComponent = this.formComponent.querySelector('input[ref="variantId"][name="id"], input[name="id"][ref="variantId"]');
        if (inFormComponent) return inFormComponent;

        const fallbackInForm = this.formComponent.querySelector('input[name="id"]');
        if (fallbackInForm) return fallbackInForm;
      }

      const closestForm = this.container.closest('form');
      if (closestForm) {
        const inClosestForm = closestForm.querySelector('input[name="id"]');
        if (inClosestForm) return inClosestForm;
      }

      return document.querySelector('product-form-component input[ref="variantId"][name="id"], product-form-component input[name="id"][ref="variantId"]');
    }

    getQuantityInput() {
      const scope = this.formComponent || this.container.closest('form');
      if (scope) {
        const input = scope.querySelector('input[name="quantity"], .quantity__input, [data-quantity-input]');
        if (input) return input;
      }
      // No document-wide fallback: cart-drawer quantity inputs share the same
      // selector and would be picked up here when the drawer is open, causing
      // the cart quantity (e.g. 10) to be used as the "add" quantity.
      return null;
    }
  }
  
  // ===== INITIALIZATION =====
  
  // PRELOAD: Fetch validation data immediately on page load (before cart opens)
  async function preloadValidation() {
    // Find customer ID from any recovercann container on the page
    const container = document.querySelector(CART_STATUS_CONTAINER_SELECTOR) || 
                      document.querySelector('[data-customer-id]');
    
    if (!container) return;
    
    const customerId = container.dataset.customerId;
    if (!customerId) return;
    
    // Already preloading or complete
    if (preloadPromise || preloadComplete) return;
    
    log(' Preloading prescription data...');
    
    preloadPromise = (async () => {
      try {
        // Fetch cart
        const cartData = await fetchFreshCart();
        
        // Build request
        const cartItems = cartData.items.map(item => ({
          variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
          productId: `gid://shopify/Product/${item.product_id}`,
          quantity: item.quantity
        }));
        
        const requestBody = {
          customerId: `gid://shopify/Customer/${customerId}`,
          cartItems,
          ...(DEBUG ? { debug: true } : {})
        };
        
        // Call validation API
        const cacheKey = buildCacheKeyForRequest(customerId, cartItems);
        const response = await postCartValidation(requestBody, {
          'X-RecoverCann-Cache-Key': cacheKey,
        });
        
        if (!response.ok) return;
        const validation = await response.json();
        
        // Cache the results - include active customer in hash
        const itemsHash = cartData.items.map(i => `${i.variant_id}:${i.quantity}`).join('|');
        const apiCustomerId = validation.superShopper?.customerId || customerId;
        const normalizedApiId = normalizeCustomerId(apiCustomerId);
        const cartHash = `${normalizedApiId}::${itemsHash}`;
        validationCache.data = validation;
        validationCache.cartHash = cartHash;
        validationCache.timestamp = Date.now();
        
        // Populate DFE cache - track which customer this is for
        const cacheCustomerId = normalizedApiId;
        if (validation.cart && validation.cart.items) {
          validation.cart.items.forEach(item => {
            const variantId = parseInt(item.variantId.replace('gid://shopify/ProductVariant/', ''));
            dfeCache.variants.set(variantId, item.dfePerUnit || 0);
          });
        }
        
        if (validation.period && validation.prescription) {
          dfeCache.customerId = cacheCustomerId;
          dfeCache.prescription = {
            monthlyLimit: validation.prescription.monthlyLimit,
            remainingInPeriod: validation.period.remainingInPeriod,
            usedInPeriod: validation.period.usedInPeriod,
            periodEnd: validation.period.endDate
          };
          dfeCache.initialized = true;
        }
        
        // Cache category limit data for local recalculation
        if (validation.limits?.categoryLimits) {
          categoryCache.categories.clear();
          for (const cat of validation.limits.categoryLimits) {
            categoryCache.categories.set(cat.handle, { title: cat.title, limit: cat.limit, used: cat.used });
          }
        }
        if (validation.limits?.productCategoryMap) {
          categoryCache.productMap.clear();
          for (const [gid, handles] of Object.entries(validation.limits.productCategoryMap)) {
            const productId = parseInt(gid.replace('gid://shopify/Product/', ''));
            categoryCache.productMap.set(productId, handles);
          }
          categoryCache.initialized = true;
          categoryCache.timestamp = Date.now();
        }
        
        preloadComplete = true;
        log(' Preload complete - cart will open instantly');
        
      } catch (error) {
        console.warn('[RecoverCann] Preload failed:', error);
      }
    })();
    
    return preloadPromise;
  }
  
  function init() {
    installCheckoutGuards();

    // Start preloading immediately
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        preloadValidation();
        initCartValidators();
        new ProductValidator();
      });
    } else {
      preloadValidation();
      initCartValidators();
      new ProductValidator();
    }

    // FALLBACK: Listen for cart:update at document level
    // If CartValidator wasn't initialized (e.g., empty cart on page load for guest),
    // try to initialize it when cart updates (first product added)
    document.addEventListener('cart:update', () => {
      if (!cartValidatorInstances.length) {
        log(' CartValidator not initialized, attempting to create...');
      }
      scheduleInitCartValidators();
    });

    document.addEventListener('recovercann:refresh', (event) => {
      log(' RecoverCann refresh event received');
      scheduleInitCartValidators();

      const forceRefresh = event.detail?.forceRefresh !== false;
      const silentRefresh = Boolean(event.detail?.silentRefresh);

      requestAnimationFrame(() => {
        cartValidatorInstances.forEach((validator) => {
          if (validator?.initialized) {
            validator.scheduleRefresh(forceRefresh, { showLoading: !silentRefresh });
          }
        });
      });
    });


    // Listen for Super Shopper changes and invalidate cache + re-fetch
    document.addEventListener('superShopper:customerSelected', () => {
      log(' Super Shopper customer selected, invalidating cache...');
      // Clear validation cache to force re-fetch with new customer
      validationCache.data = null;
      validationCache.cartHash = null;
      validationCache.timestamp = 0;
      dfeCache.initialized = false;
      dfeCache.prescription = null;
      dfeCache.customerId = null;
      categoryCache.initialized = false;
      categoryCache.timestamp = 0;
      
      // Re-fetch immediately via CartValidator (avoids dispatching cart:update
      // which crashes component-cart-items.js expecting event.detail.sections)
      requestAnimationFrame(() => {
        log(' Re-fetching validation for selected patient...');
        cartValidatorInstances.forEach((validator) => {
          if (validator && validator.initialized) {
            validator.scheduleRefresh(true);
          }
        });
      });
    });

    document.addEventListener('superShopper:disabled', () => {
      log(' Super Shopper disabled, invalidating cache...');
      // Clear validation cache
      validationCache.data = null;
      validationCache.cartHash = null;
      validationCache.timestamp = 0;
      dfeCache.initialized = false;
      dfeCache.prescription = null;
      dfeCache.customerId = null;
      categoryCache.initialized = false;
      categoryCache.timestamp = 0;
      
      // Re-fetch immediately via CartValidator
      requestAnimationFrame(() => {
        log(' Re-fetching validation for staff user...');
        cartValidatorInstances.forEach((validator) => {
          if (validator && validator.initialized) {
            validator.scheduleRefresh(true);
          }
        });
      });
    });
  }

  function initCartValidators() {
    const containers = getCartStatusContainers();
    containers.forEach((container) => {
      if (!hasRenderableCartStatus(container)) {
        log('initCartValidators:skip-non-renderable', {
          context: container.dataset.rcContext || 'unknown',
          hasLoading: Boolean(container.querySelector('.recovercann-loading')),
          hasContent: Boolean(container.querySelector('.recovercann-content')),
          customerId: container.dataset.customerId || null,
        });
        delete container.dataset.rcValidatorInitialized;
        return;
      }
      if (container.dataset.rcValidatorInitialized === 'true') return;
      if (!container.dataset.rcInstanceId) {
        cartValidatorInstanceCounter += 1;
        container.dataset.rcInstanceId = String(cartValidatorInstanceCounter);
      }
      const validator = new CartValidator(container);
      if (validator.initialized) {
        cartValidatorInstances.push(validator);
        log(' CartValidator initialized for container');
      }
    });
  }

  let initCartValidatorsTimer = null;
  function scheduleInitCartValidators() {
    const runInit = () => initCartValidators();

    // Morph/section rendering can settle on the next frame(s)
    requestAnimationFrame(() => {
      runInit();
      requestAnimationFrame(() => {
        runInit();
      });
    });

    if (initCartValidatorsTimer) {
      clearTimeout(initCartValidatorsTimer);
    }

    // Final retry if morph/update landed slightly later
    initCartValidatorsTimer = setTimeout(() => {
      initCartValidatorsTimer = null;
      runInit();
    }, 300);
  }
  
  return { init, preload: preloadValidation };
})();

// Auto-initialize
RecoverCann.init();
